import { formatQuantityDisplay } from '@/composables/useQuantity'
import { formatUnitPriceCurrency } from '@/utils/moneyScale'
import {
  getSaleCashRoundingAdjustment,
  shouldShowCashRoundingOnReceipt
} from '@/utils/receiptCashRoundingDisplay'
import { tabEntryReceiptLineItemsFromContext } from '@/utils/tabEntryReceiptItems'
import {
  buildTabPaymentReceiptTaxLines,
  shouldShowTabPaymentReceiptTaxBreakdown
} from '@/utils/tabPaymentReceiptTax'
import { resolveSaleCustomerReceiptLabel } from '@/utils/saleCustomerDisplay'

function fmtMoney (n) {
  const x = Number(n) || 0
  return `$${x.toFixed(2)}`
}

function fmtMinor (minor) {
  return fmtMoney((Number(minor) || 0) / 100)
}

function padBetween (left, right, width) {
  const L = String(left)
  const R = String(right)
  const space = width - L.length - R.length
  if (space <= 0) {
    return `${L} ${R}`
  }
  return `${L}${' '.repeat(space)}${R}`
}

function wrapToWidth (str, width) {
  const s = String(str || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const words = s.split(/\s+/)
  const lines = []
  let cur = ''
  for (const w of words) {
    if (!w) continue
    const next = cur ? `${cur} ${w}` : w
    if (next.length <= width) {
      cur = next
    } else {
      if (cur) lines.push(cur)
      if (w.length <= width) {
        cur = w
      } else {
        for (let i = 0; i < w.length; i += width) {
          lines.push(w.slice(i, i + width))
        }
        cur = ''
      }
    }
  }
  if (cur) lines.push(cur)
  return lines.length ? lines : ['']
}

function sectionBanner (cols, char = '=') {
  return `${char.repeat(Math.min(cols, 42))}\r\n`
}

function dashedRule (cols) {
  return `${'-'.repeat(Math.min(cols, 42))}\r\n`
}

/**
 * @param {object|null|undefined} ctx tab_payment_context
 * @returns {boolean}
 */
export function isStructuredTabPaymentContext (ctx) {
  if (!ctx || ctx.has_full_snapshot === false) {
    return false
  }
  return ctx.balance_before_minor != null
}

/**
 * @param {object} sale
 * @returns {boolean}
 */
export function isStructuredTabPaymentReceipt (sale) {
  return isStructuredTabPaymentContext(sale?.tab_payment_context)
}

/**
 * @param {object} ctx
 * @returns {boolean}
 */
export function shouldShowTabPaymentReceiptItems (ctx) {
  if (!ctx) return false
  return tabEntryReceiptLineItemsFromContext(ctx).length > 0
}

/**
 * @param {object} ctx
 * @returns {number}
 */
export function tabPaymentCreditOnTabMinor (ctx) {
  const current = Number(ctx?.current_total_minor || 0)
  return current < 0 ? Math.abs(current) : 0
}

/**
 * Tip, convenience fee, and total charged for a tab pay-down sale (from sale totals / payments).
 *
 * @param {object|null|undefined} sale
 * @returns {{
 *   tabPaymentMinor: number,
 *   tipMinor: number,
 *   feeMinor: number,
 *   roundingMinor: number,
 *   totalChargedMinor: number,
 *   hasChargeBreakdown: boolean
 * }}
 */
export function resolveTabPaymentSaleCharges (sale) {
  const empty = {
    tabPaymentMinor: 0,
    tipMinor: 0,
    feeMinor: 0,
    roundingMinor: 0,
    totalChargedMinor: 0,
    hasChargeBreakdown: false
  }
  if (!sale) {
    return empty
  }

  const ctx = sale.tab_payment_context
  const tabPaymentMinor = ctx?.payment_amount_minor != null
    ? Number(ctx.payment_amount_minor) || 0
    : 0

  let tipMinor = Math.round(Number(sale.tip_total || 0) * 100)
  let feeMinor = Math.round(Number(sale.convenience_fee_total || 0) * 100)

  const payments = Array.isArray(sale.payments) ? sale.payments : []
  if (tipMinor <= 0) {
    tipMinor = Math.round(payments.reduce((sum, p) => sum + Number(p.tip_amount || 0), 0) * 100)
  }
  if (feeMinor <= 0) {
    feeMinor = Math.round(payments.reduce((sum, p) => sum + Number(p.fee_amount || 0), 0) * 100)
  }

  const roundingAdjustment = shouldShowCashRoundingOnReceipt(sale)
    ? getSaleCashRoundingAdjustment(sale)
    : 0
  const roundingMinor = Math.round(Math.abs(roundingAdjustment) * 100)

  let totalChargedMinor = Math.round(Number(sale.total || 0) * 100)
  if (totalChargedMinor <= 0 && tabPaymentMinor > 0) {
    totalChargedMinor = tabPaymentMinor + tipMinor + feeMinor + roundingMinor
  }

  const hasChargeBreakdown = tipMinor > 0
    || feeMinor > 0
    || roundingMinor > 0
    || (totalChargedMinor > 0 && tabPaymentMinor > 0 && totalChargedMinor !== tabPaymentMinor)

  return {
    tabPaymentMinor,
    tipMinor,
    feeMinor,
    roundingMinor,
    totalChargedMinor,
    hasChargeBreakdown
  }
}

function appendTabPaymentSaleChargeRows (ops, sale, ctx, L, cols, op, padBetweenFn) {
  const charges = resolveTabPaymentSaleCharges(sale)
  const tabMinor = charges.tabPaymentMinor > 0
    ? charges.tabPaymentMinor
    : (ctx?.payment_amount_minor != null ? Number(ctx.payment_amount_minor) || 0 : charges.totalChargedMinor)

  const tabLabel = charges.hasChargeBreakdown
    ? (L.tabPaymentAmount || L.amountPaid || 'Tab payment')
    : (L.amountPaid || 'Amount paid')

  ops.push(
    op({
      type: 'text',
      text: `${padBetweenFn(tabLabel, fmtMinor(tabMinor), cols)}\r\n`,
      ...(charges.hasChargeBreakdown ? {} : { doubleWidth: true, thick: 1 })
    })
  )

  if (!charges.hasChargeBreakdown) {
    return
  }

  if (charges.tipMinor > 0) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.tip || 'Tip', fmtMinor(charges.tipMinor), cols)}\r\n`
      })
    )
  }

  if (charges.feeMinor > 0) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.fee || 'Fee', fmtMinor(charges.feeMinor), cols)}\r\n`
      })
    )
  }

  if (charges.roundingMinor > 0) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.pennyRounding || 'Penny rounding', fmtMinor(charges.roundingMinor), cols)}\r\n`
      })
    )
  }

  if (charges.totalChargedMinor > 0 && charges.totalChargedMinor !== tabMinor) {
    ops.push(op({ type: 'text', text: dashedRule(cols) }))
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.totalCharged || L.total || 'Total charged', fmtMinor(charges.totalChargedMinor), cols)}\r\n`,
        doubleWidth: true,
        thick: 1
      })
    )
  }
}

function appendTabEntryItems (ops, ctx, L, cols, op, addLines, padBetweenFn) {
  const rows = tabEntryReceiptLineItemsFromContext(ctx)
  for (const row of rows) {
    const name = row.name || L.unknownProduct || 'Item'
    const qty = formatQuantityDisplay(row.quantity, {
      allowFractional: row.product?.allow_fractional ?? row.allow_fractional ?? null
    })
    addLines(`${qty} x ${formatUnitPriceCurrency(row.price, row.price_decimals ?? row.product?.price_decimals ?? 2)}`)
    for (const nl of wrapToWidth(name, cols)) {
      addLines(nl)
    }
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.lineTotal || 'Line', fmtMoney(row.total), cols)}\r\n`,
        thick: 1
      })
    )
  }
}

function appendTabPaymentTaxBreakdown (ops, ctx, L, cols, op, padBetweenFn) {
  if (!shouldShowTabPaymentReceiptTaxBreakdown(ctx)) {
    return
  }

  const subtotalLabel = L.subtotal || 'Subtotal'
  const goodsMinor = ctx.goods_subtotal_minor != null
    ? Number(ctx.goods_subtotal_minor) || 0
    : tabEntryReceiptLineItemsFromContext(ctx).reduce(
      (sum, row) => sum + Math.round((Number(row.subtotal) || 0) * 100),
      0
    )

  if (goodsMinor > 0) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(subtotalLabel, fmtMinor(goodsMinor), cols)}\r\n`
      })
    )
  }

  const taxLines = buildTabPaymentReceiptTaxLines(ctx, { genericTaxLabel: L.tax || 'Tax' })
  for (const line of taxLines) {
    const indent = line.isDetail ? '  ' : ''
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(`${indent}${line.label}`, fmtMinor(line.amountMinor), cols)}\r\n`
      })
    )
  }
}

function appendTabAccountSection (ops, ctx, L, cols, op, addLines, padBetweenFn) {
  ops.push(op({ type: 'text', text: '\r\n' }))
  ops.push(op({ type: 'text', text: sectionBanner(cols) }))
  addLines((L.tabAccountSection || 'TAB ACCOUNT').toUpperCase(), { thick: 1 })
  ops.push(op({ type: 'text', text: sectionBanner(cols) }))

  if (shouldShowTabPaymentReceiptItems(ctx)) {
    addLines(L.itemsChargedToTab || 'Items charged to tab:')
    ops.push(op({ type: 'text', text: '\r\n' }))
    appendTabEntryItems(ops, ctx, L, cols, op, addLines, padBetweenFn)
    ops.push(op({ type: 'text', text: dashedRule(cols) }))
  }

  appendTabPaymentTaxBreakdown(ops, ctx, L, cols, op, padBetweenFn)

  if (ctx.current_total_minor != null) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.tabChargesTotal || 'Tab charges total', fmtMinor(ctx.current_total_minor), cols)}\r\n`
      })
    )
  }

  const creditsMinor = Number(ctx.credits_minor || 0)
  if (creditsMinor > 0) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.credits || 'Credits', `- ${fmtMinor(creditsMinor)}`, cols)}\r\n`
      })
    )
  }

  if ((ctx.payments_before_minor || 0) > 0) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.priorPayments || 'Prior payments', `- ${fmtMinor(ctx.payments_before_minor)}`, cols)}\r\n`
      })
    )
  }

  if (ctx.balance_before_minor != null) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.tabBalanceBefore || 'Balance before', fmtMinor(ctx.balance_before_minor), cols)}\r\n`
      })
    )
  }
}

function appendThisPaymentSection (ops, ctx, L, cols, op, addLines, padBetweenFn, sale = null) {
  ops.push(op({ type: 'text', text: '\r\n' }))
  ops.push(op({ type: 'text', text: sectionBanner(cols) }))
  addLines((L.thisPaymentSection || 'THIS PAYMENT').toUpperCase(), { thick: 1 })
  ops.push(op({ type: 'text', text: sectionBanner(cols) }))

  if (ctx.payment_amount_minor != null || sale) {
    appendTabPaymentSaleChargeRows(ops, sale, ctx, L, cols, op, padBetweenFn)
  }

  ops.push(op({ type: 'text', text: '\r\n' }))

  const balanceAfter = Number(ctx.balance_after_minor ?? 0)
  const creditMinor = tabPaymentCreditOnTabMinor(ctx)

  if (balanceAfter > 0) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.tabBalanceAfter || 'Remaining balance', fmtMinor(balanceAfter), cols)}\r\n`,
        thick: 1
      })
    )
  } else if (creditMinor > 0) {
    const creditLabel = (L.creditDisplay || 'Cr {amount}').replace('{amount}', fmtMinor(creditMinor))
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.creditBalance || 'CREDIT', creditLabel, cols)}\r\n`,
        thick: 1
      })
    )
  } else {
    ops.push(
      op({
        type: 'text',
        text: `${padBetweenFn(L.paidInFull || 'PAID IN FULL', fmtMinor(0), cols)}\r\n`,
        doubleWidth: true,
        thick: 1
      })
    )
  }
}

/**
 * Structured tab payment receipt body (TAB ACCOUNT + THIS PAYMENT).
 */
export function appendStructuredTabPaymentReceiptBody (ops, ctx, L, cols, op, addLines, sale = null) {
  appendTabAccountSection(ops, ctx, L, cols, op, addLines, padBetween)
  appendThisPaymentSection(ops, ctx, L, cols, op, addLines, padBetween, sale)
}

/**
 * Lightweight tab payment receipt (no full snapshot).
 */
export function appendLightweightTabPaymentReceiptBody (ops, ctx, L, cols, op, addLines, sale = null) {
  ops.push(op({ type: 'text', text: '\r\n' }))
  ops.push(op({ type: 'text', text: sectionBanner(cols) }))
  addLines((L.thisPaymentSection || 'THIS PAYMENT').toUpperCase(), { thick: 1 })
  ops.push(op({ type: 'text', text: sectionBanner(cols) }))

  if (ctx?.payment_amount_minor != null || sale) {
    appendTabPaymentSaleChargeRows(ops, sale, ctx, L, cols, op, padBetween)
  }
}

/**
 * Tab payment header lines after business block.
 */
export function appendTabPaymentReceiptHeader (ops, sale, ctx, L, cols, op, addLines, businessInfo = null) {
  addLines(L.tabPaymentReceiptTitle || 'Tab Payment Receipt', { thick: 1 })
  addLines(`${L.sale || 'Sale'} #${sale.id}`)
  const created = sale.created_at ? new Date(sale.created_at) : new Date()
  addLines(`${L.date || 'Date'}: ${created.toLocaleString()}`)
  if (sale.sale_source === 'terminal_feed') {
    addLines(`${L.saleSource || 'Source'}: ${L.saleSourceTerminalFeed || 'Terminal (FEED)'}`)
  }
  if (sale.user?.name && businessInfo?.show_cashier_on_receipt !== false) {
    addLines(`${L.cashier || 'Cashier'}: ${sale.user.name}`)
  }
  ops.push(op({ type: 'text', text: '\r\n' }))
  if (ctx.tab_id != null) {
    addLines(`${L.tabNumber || 'Tab'} #${ctx.tab_id}`)
  }
  if (ctx.owner_label) {
    addLines(`${L.tabOwner || 'Owner'}: ${ctx.owner_label}`)
  }
  const customerLabel = L.customer || 'Customer'
  const walkIn = L.walkInCustomer || 'Walk-In'
  addLines(`${customerLabel}: ${resolveSaleCustomerReceiptLabel(sale, walkIn)}`)
}

export { padBetween as tabPaymentPadBetween, fmtMinor as tabPaymentFmtMinor }
